import { createContext, useState, useEffect, type ReactNode } from "react";
import type { AuthState } from "../types";
import { AuthService } from "@/features/auth/services/auth.service";
import { jwtDecode } from "jwt-decode";
import { PERMISSIONS } from "@/constants/roles";

interface JwtPayload {
  role: string;
  sub: string;
  iat: number;
  exp: number;
}

interface AuthContextType extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  role: string | null;
  isAdmin: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadStoredAuth = () => {
      try {
        const storedToken = localStorage.getItem("authToken");

        if (storedToken) {
          try {
            const decoded = jwtDecode<JwtPayload>(storedToken);
            const now = Date.now() / 1000;

            if (decoded.exp && decoded.exp < now) {
              localStorage.removeItem("authToken");
              localStorage.removeItem("authUser");
              return;
            }

            setToken(storedToken);
          } catch {
            localStorage.removeItem("authToken");
            localStorage.removeItem("authUser");
          }
        }
      } catch (error) {
        console.log(error);
        localStorage.removeItem("authToken");
        localStorage.removeItem("authUser");
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredAuth();
  }, []);

  const login = async (username: string, password: string): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await AuthService.login(username, password);

      setToken(response.token);

      localStorage.setItem("authToken", response.token);
    } catch (error) {
      console.error("Erro no login:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setToken(null);
    localStorage.removeItem("authToken");
  };

  const hasPermission = (permission: string): boolean => {
    if (!token) return false;

    try {
      const decoded = jwtDecode<JwtPayload>(token);
      const role = decoded.role.toLowerCase() as "admin" | "user";

      const userPermissions = PERMISSIONS[role] as readonly string[];

      return userPermissions.includes(permission);
    } catch (error) {
      console.error("Erro ao decodificar token:", error);
      return false;
    }
  };

  const getRole = (): string | null => {
    if (!token) return null;

    try {
      const decoded = jwtDecode<JwtPayload>(token);
      return decoded.role;
    } catch (error) {
      console.error("Erro ao decodificar token:", error);
      return null;
    }
  };

  const role = getRole();
  const isAdmin = role?.toLowerCase() === "admin";

  const value: AuthContextType = {
    token,
    isAuthenticated: !!token,
    isLoading,
    login,
    logout,
    hasPermission,
    role,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
